import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Pill } from 'components/Pill/Pill'
import { getUserData, updateUser } from 'redux/reducers/users'
import { useDispatch, useSelector } from 'react-redux'
import { MyTextField } from 'components/ReactHookForm/MyTextField'
import { Button } from 'components/Button/Button'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { MyDialog } from 'components/MyDialog'
import { medicareApiClient } from 'clients/medicareApiClient'
import { AddEditEmailAddressForm } from 'models/forms/AddEditEmailAddressForm'
import { setModal } from 'redux/reducers/modal'
import { ILoadingStatus } from 'models/LoadingStatus'
import { prefixErrorText } from 'utils/react-hook-form'
import { useMountedState } from 'hooks/useMountedState'
import { Alert } from '@cmsgov/ds-medicare-gov'
import { useModalCloseEvent } from 'components/MyDialog/useModalCloseEvent'
import { MySpinner } from 'components/MySpinner/MySpinner'
import { Component } from '@cms-max/medicare-api-sdk'

export type EmailAddressModalProps = {
  onClose: () => void
}

export const EmailAddressModal: FC<EmailAddressModalProps> = ({ onClose }) => {
  const { t } = useTranslation('addEditEmail')
  const userData = useSelector(getUserData)
  const methods = useForm<AddEditEmailAddressForm>({
    mode: 'onTouched',
    reValidateMode: 'onChange'
  })

  const dispatch = useDispatch()
  const [loadingState, setLoadingState] = useMountedState<ILoadingStatus>('standby')

  const onSubmit: SubmitHandler<AddEditEmailAddressForm> = async (form) => {
    setLoadingState('loading')
    try {
      await medicareApiClient.asyncUpdateBeneficiaryEmail({
        email: form.email
      })
      dispatch(updateUser({
        email: form.email
      }))
      setLoadingState('finished')
      dispatch(setModal({ modalType: undefined }))
    } catch (error) {
      // set error state and display the alert on the modal
      setLoadingState('error')
    }
  }

  // These values match with the i18n keys
  const formType = userData.user?.email ? 'edit-form' : 'add-form'

  const handleClose = useModalCloseEvent('accountSettings', 'accountsettings_emsnclose_click', onClose)

  return (
    <MyDialog
      heading={t('heading')}
      containerId='medicare-my-add-edit-email'
      closeButtonAriaLabel={t('Close')}
      onClose={handleClose}
    >
      <>
        <p className='my-u-pb--8'>{t('subtitle-paragraph')}</p>
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
          >
            {userData.user?.email && (
              <div className='my-u-pb--16'>
              <h2 className='ds-text-heading--xl'>{t('current-email-address')}</h2>
              <div className='ds-u-overflow--auto'>
                <Pill>{userData.user.email}</Pill>
              </div>
            </div>
            )}
            <div className="my-u-mb--14">
              <h2 className='ds-text-heading--xl'>{t(`${formType}.enter-email-address-heading`)}</h2>
              <MyTextField
                name='email'
                rules={{
                  required: { value: true, message: t('errors.enterEmail.empty') },
                  pattern: {
                    value: /^\w+([-+_.']\w+)*@\w+([-.]\w+)*(\.[A-Za-z]{2,})$/,
                    message: t('errors.enterEmail.invalid')
                  }
                }}
                addErrorPrefix
                label={t(`${formType}.enter-email-address`)}
                labelClassName='my-u-text-transform--default'
                defaultValue=''
              />
              <MyTextField
                name='confirmEmail'
                rules={{
                  required: { value: true, message: t('errors.confirmEmail.empty') },
                  validate: {
                    email: (value) => (value === methods.getValues('email') || t('errors.confirmEmail.matching') as string)
                  }
                }}
                addErrorPrefix
                label={t(`${formType}.confirm-email-address`)}
                labelClassName='my-u-text-transform--default'
                defaultValue=''
              />
            </div>
            {loadingState === 'error' && (
              <Alert variation='error' role='alert'>
                <p>
                  {prefixErrorText(t('errors.submitFailure.message'))}
                </p>
              </Alert>
            )}
            <div className='ds-c-dialog__actions'>
              <div className='ds-l-form-row'>
                <div className='ds-l-col--12 ds-l-sm-col--auto my-u-my--2'>
                  <Button
                    type='submit'
                    className='ds-c-button ds-c-button--solid my-u-py--6 my-u-width--full'
                    siteActivityProps={{
                      core: {
                        component: Component.AccountSettings,
                        elementId: 'accountsettings_emailsave_click',
                        coreClickData: {
                          linkText: t('save-changes')
                        }
                      }
                    }}
                  >
                    {loadingState === 'loading' && (
                      <MySpinner size='small' inverse isInButton/>
                    )}
                    {t('save-changes')}
                  </Button>
                </div>
                <div className='ds-l-col--12 ds-l-sm-col--auto my-u-my--2'>
                  <Button
                    onClick={onClose}
                    className='ds-c-button my-u-py--6 my-u-width--full'
                    siteActivityProps={{
                      core: {
                        component: Component.AccountSettings,
                        elementId: 'accountsettings_emailclose_click',
                        coreClickData: {
                          linkText: t('cancel')
                        }
                      }
                    }}
                  >
                    {t('cancel')}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </FormProvider>
      </>
    </MyDialog>
  )
}
